/**
 * GucSizerScreen — the GUC Sizer v2.
 *
 * A SECOND, ground-up-dedicated sizer that behaves identically to the RTL/GUC
 * page — same backend, same rules, same derivations, same workbench chrome —
 * but presents a LEAN results surface. The RTL page is deliberately verbose: it
 * itemizes every reduction, citation, provenance chip and advisory because
 * someone is auditing the engine. This one is for someone pricing a deal. Both
 * exist; neither replaces the other.
 *
 * Source of truth: eastview-os `frontend_documentation/GUC_SIZER_V2_FRONTEND_SPEC.md`.
 *
 * The non-negotiables it inherits (spec §1):
 *   1. The backend is the only calculator. The one exception is the declared
 *      client-side mirror in `gucLoanMath` — a loan-size PREVIEW, labelled an
 *      estimate, never sent back, superseded the moment a response lands.
 *   2. `POST /pricing/scenario/rtl` in loan-model vocabulary, with `sizerV2:
 *      true` so telemetry can tell the two surfaces apart (the backend treats
 *      unknown flags as inert).
 *   3. Amounts read in build-up order, with live / priced / stale marking.
 *   4. Every reprice suggestion writes back into the actual form input.
 *   5. Formatted, always. Never `String()` a number into copy.
 *   6. Local time for every datetime.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import SaveIcon from "@mui/icons-material/Save";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import RffFormSpy from "../../components/finalForm/shared/RffFormSpy";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { sizerApi, readScenarioId } from "../../api/sizerApi";

import GucLoanDetailsSection from "./sections/GucLoanDetailsSection";
import GucGuarantorsSection, { newGuarantor } from "./sections/GucGuarantorsSection";
import GucPropertySection, { newGucProperty } from "./sections/GucPropertySection";
import EntitySection from "../SizerScreen/sections/EntitySection";
import AmountsStrip from "./components/AmountsStrip";
import GucResultsPanel from "./components/GucResultsPanel";
import ExplainResultPanel from "../sizersShared/workspace/ExplainResultPanel";
import RequestExceptionModal from "./components/RequestExceptionModal";
import { GucKpis, GucPanels, ZeroExperienceNote } from "./components/GucReview";
import SizerWorkspace from "../sizersShared/workspace/SizerWorkspace";
import {
  SIZE_BY,
  SIZE_BY_FIELD,
  sizeByFor,
} from "../sizersShared/LeverageOrAmountField";
import {
  WORKSPACE_MODES,
  pricedToastText,
  useWorkspaceMode,
} from "../sizersShared/workspace/workspaceModel";
import { scenarioInputSummary } from "../sizersShared/scenarioModel";
import { Callout } from "../sizersShared/sizerResultUi";
import { fmtMoney, fmtPct, isoDateFromToday } from "../sizersShared/sizerFormat";
import { normalizeLegacyDelayedPurchase } from "../../utils/loans/delayedPurchase";
import {
  stripFormOnlyKeys,
  FormCard,
  PrimaryButton,
  SecondaryButton,
  ActionNote,
  SizerDevBar,
  SizerPayloadPanel,
  useSizerDevTools,
  scrollSizerToTop,
  SZ,
} from "../sizersShared/sizerUi";
import {
  gucGuarantyExceptionPrefill,
  isGucZeroExperienceBlock,
  pendingExceptionCodes,
} from "./gucConstants";
import { budgetShareCure, estimateAmounts } from "./gucLoanMath";
import { shownAmounts } from "../sizersShared/sizerAmounts";
import { plainSummaryText } from "./gucPlainSummary";
import {
  propertyFromStored,
  sizingInputFor,
} from "../sizersShared/FinancedCostsFields";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";

const GUC_PRODUCT = "Ground Up Construction";

/**
 * The form opens EMPTY apart from the two things that are not values: the
 * product (this IS the GUC sizer) and one blank guarantor + one blank property
 * so there is a card to type into rather than an empty section and an Add
 * button. No FICO, no leverage, no amounts — a defaulted number prices a deal
 * nobody entered, and prices it optimistically.
 */
const EMPTY_SCENARIO = {
  loanProductType: GUC_PRODUCT,
  pipelineTarget: "underwriting_pipeline",
  borrowers: [newGuarantor()],
  subjectProperties: [newGucProperty()],
};

/**
 * SCENARIO-STAGE STUBS — accepted when pricing, stripped when saving.
 *
 * `/scenario/rtl` turns each into a `dataChecks[]` entry to reconcile against
 * real data in processing. The normalizer does NOT remove them, and the LOS
 * models are `extra=allow`, so posting the same body to `/underwriting/create_loan`
 * would persist them on the deal as junk.
 */
const SCENARIO_ONLY_KEYS = ["numberOfEntityMembers", "gcCompletionGuaranty", "sizerV2"];
const SCENARIO_ONLY_BORROWER_KEYS = ["backgroundFlagsPresent"];

/**
 * Form values → the wire body.
 *
 * Shape corrections only — nothing is computed, defaulted or invented. The form
 * holds the borrowing entity under a single `entity` key because that reads
 * naturally as a section; the wire shape is the loan's flat `entities[]` with
 * `borrowerEntity: true` marking the primary one.
 *
 * `forSave` additionally maps the scenario-only GC guaranty toggle onto the
 * field that actually exists on a loan —
 * `generalContractors[0].providesCompletionGuaranty` (spec §4) — and attaches
 * `sizerPricingSummary` so the deal's first comment tells the story of how it
 * was priced.
 */
function buildPayload(values, { forSave = false, result = null } = {}) {
  const { entity, ...rest } = values;

  const payload = {
    ...rest,
    loanProductType: GUC_PRODUCT,
    borrowers: (values.borrowers || []).map((b) => {
      if (!forSave || !b) return b;
      const out = { ...b };
      SCENARIO_ONLY_BORROWER_KEYS.forEach((k) => delete out[k]);
      return out;
    }),
  };

  if (entity?.entityName || entity?.entityType) {
    payload.entities = [{ ...entity, borrowerEntity: true }];
  }
  // Form-only answers (`_delayedPurchase`, `_sizeBy`) never post — loanPurpose
  // and `sizingInput` carry them.
  stripFormOnlyKeys(payload);

  if (!forSave) {
    // Which field the user drove (§12b): in leverage mode a financed reserve
    // grosses the initial loan amount up; an entered amount is taken as is.
    const sizingInput = sizingInputFor(values, values[SIZE_BY_FIELD]);
    if (sizingInput) payload.sizingInput = sizingInput;
    // Telemetry only — it tells the two sizer surfaces apart in the persisted
    // scenario. The engine reads nothing from it.
    payload.sizerV2 = true;
    return payload;
  }

  SCENARIO_ONLY_KEYS.forEach((k) => delete payload[k]);
  if (values.gcCompletionGuaranty) {
    payload.generalContractors = [{ providesCompletionGuaranty: true }];
  }
  const summary = plainSummaryText(result, values);
  if (summary) payload.sizerPricingSummary = summary;
  return payload;
}

/**
 * The inverse — a stored scenario back into form values, for a resume.
 *
 * The stored scenario is the NORMALIZED one: `_normalize_rtl_scenario` writes
 * `baseLoanAmount`, `totalHoldback` and `totalLoanAmount` onto it, and derives
 * each only when it is blank. Seeding them back into the form would plant an
 * override nobody set — changing leverage, the budget or the financed share
 * after a resume would then move nothing. So the two derived totals are always
 * dropped, and `baseLoanAmount` is dropped when leverage is present (a reprice
 * suggestion that sets the advance clears leverage, so an advance that sits
 * beside a leverage is the normalizer's, not the user's).
 */
function payloadToValues(stored) {
  if (!stored) return null;
  // A legacy "Delayed Purchase" purpose reads as cash-out + the flag (REQ-106).
  const scenario = normalizeLegacyDelayedPurchase(stored);
  const {
    entities,
    sizerV2,
    totalHoldback,
    totalLoanAmount,
    baseLoanAmount,
    // Re-derived from the form's own leverage / amount choice on every price.
    sizingInput,
    ...rest
  } = scenario;
  const leverageSet =
    rest.sizerInitialRequestedLeverage != null && rest.sizerInitialRequestedLeverage !== "";
  const entity =
    (entities || []).find((e) => e?.borrowerEntity) || (entities || [])[0] || null;
  return {
    ...rest,
    ...(!leverageSet && baseLoanAmount != null ? { baseLoanAmount } : null),
    // Sized by leverage unless only an advance was set (then by amount).
    [SIZE_BY_FIELD]: sizeByFor({
      sizerInitialRequestedLeverage: rest.sizerInitialRequestedLeverage,
      baseLoanAmount: leverageSet ? null : baseLoanAmount,
    }),
    loanProductType: GUC_PRODUCT,
    ...(entity ? { entity } : null),
    borrowers: rest.borrowers?.length ? rest.borrowers : [newGuarantor()],
    subjectProperties: rest.subjectProperties?.length
      ? rest.subjectProperties.map(propertyFromStored)
      : [newGucProperty()],
  };
}

/**
 * The cures, read off the response.
 *
 * `suggestions[]` carries the per-over-metric rows and up to two measured
 * alternatives. The BINDING amount is the smallest `baseLoanAmountAtMax` across
 * the breached metrics — the largest advance that satisfies every one of them,
 * which is what "clears it" means and which the engine has already picked as its
 * own `min` for the alternatives.
 *
 * The 2.5% and 5% steps sit below it. The 2.5% one is the engine's own
 * `one_step_below_max` row where it came back (so it carries a MEASURED
 * `passing`); the 5% is an input suggestion with nothing asserted about it —
 * both only mean something once re-priced, which is what the buttons do.
 */
function readCures(result, values) {
  if (!result) return null;
  const suggestions = result.suggestions || [];
  const metricRows = suggestions.filter((s) => s.metric);
  if (!metricRows.length) return null;

  const binding = Math.min(...metricRows.map((r) => r.baseLoanAmountAtMax));
  const bindingRow = metricRows.find((r) => r.baseLoanAmountAtMax === binding);
  const engineStep = suggestions.find((s) => s.type === "one_step_below_max");

  const steps = [
    {
      label: "2.5%",
      amount: engineStep ? engineStep.baseLoanAmount : Math.round(binding * 0.975),
      passing: engineStep ? engineStep.passing : null,
    },
    { label: "5%", amount: Math.round(binding * 0.95), passing: null },
  ].filter((s) => s.amount > 0);

  /* The budget-share lever is only a lever when a TOTAL-loan metric is what
     broke: LTV and LTC measure the advance, and financing less of the budget
     does not move either one. */
  const totalMetricRow = metricRows.find((r) => ["LTARV", "TLTC"].includes(r.metric));
  const advance = Number(values?.baseLoanAmount) || estimateAmounts(values).advance;
  const budgetShare = totalMetricRow
    ? budgetShareCure({
        values,
        advance,
        maxTotalLoan: totalMetricRow.maxLoanAtMax,
      })
    : null;

  return { binding, bindingMetric: bindingRow?.metric, steps, budgetShare };
}


/**
 * The dev-only prefill. Every selection is a value the field actually offers and
 * every field name is the canonical one, so the demo can never itself trip a
 * payload warning. Deliberately a deal that PRICES rather than one that fails:
 * a demo that opens on four breached caps teaches the wrong thing about the
 * screen.
 */
const DEMO_SCENARIO = {
  loanProductType: GUC_PRODUCT,
  loanPurpose: "Purchase",
  termMonths: 18,
  sizerInitialRequestedLeverage: 0.65,
  financedBudgetPercent: 1.0,
  originationPoints: 0.02,
  pipelineTarget: "underwriting_pipeline",
  numberOfEntityMembers: 1,
  entity: { entityName: "Demo Build Co LLC", entityType: "LLC" },
  borrowers: [
    {
      ...newGuarantor(),
      firstName: "Demo",
      lastName: "Guarantor",
      ficoMid: 745,
      citizenshipStatus: "US Citizen",
      entityPercentOwnership: 1.0,
      cashLiquidity: 400000,
      otherLiquidity: 150000,
      experience: { ground_up_completed_and_sold: 5 },
    },
  ],
  subjectProperties: [
    {
      ...newGucProperty(),
      address: { zip: "78704", state: "TX" },
      propertyType: "SFR",
      purchasePrice: 300000,
      // ~30 days out (see the RTL demo): a fixed date ages into the past.
      purchaseDate: isoDateFromToday(30),
      asIsValue: 300000,
      constructionBudget: 450000,
      arv: 1050000,
      completedWorkHardCosts: 0,
      completedWorkSoftCosts: 25000,
      approvedPermitsAndPlans: true,
      propertyStatusAtOrigination: "Raw land with approved plans and utilities in place",
      closingCosts: 12000,
      financedClosingCosts: false,
      interestReserveMonths: 6,
      financedReserve: true,
      residentialSqFootage: 2600,
      numberOfUnits: 1,
    },
  ],
};

export default function GucSizerScreen() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [pricePending, setPricePending] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [result, setResult] = useState(null);
  const [savePending, setSavePending] = useState(false);
  const [pricedAt, setPricedAt] = useState(null);
  const [pricedValues, setPricedValues] = useState(null);
  // What was actually POSTED — the strip reads the priced amounts off the
  // scenario the backend derived them onto, rather than recomputing them.
  const [pricedScenario, setPricedScenario] = useState(null);
  // The whole scenario doc, for `exceptionRequests[]`. Refetched after a run and
  // after filing, so a pending blocker survives a reprice and a reload.
  const [scenarioDoc, setScenarioDoc] = useState(null);

  const scenarioIdRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const resumeId = readScenarioId(searchParams.get("scenario"));
  const resumeVersion = Number(searchParams.get("version")) || null;
  // Review | Edit inputs — `?mode=`, Review by default on a resumed scenario.
  const [mode, setMode] = useWorkspaceMode(Boolean(resumeId));
  const [seed, setSeed] = useState(null);
  const [loadedVersion, setLoadedVersion] = useState(null);

  const setVersionParam = (version) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (version) next.set("version", String(version));
        else next.delete("version");
        return next;
      },
      { replace: true },
    );

  /* Set right before this screen moves its OWN URL after a price (blank →
     `?scenario=<id>&mode=edit`, or dropping `&version=`). A run persists a
     scenario and the id goes into the URL so a reload comes back to it (spec
     acceptance §6.1: a filed exception has to survive a reprice AND a reload).
     But writing the param changes `resumeId`, which the resume effect watches,
     and a resume RESETS THE FORM onto the stored payload — the form already
     holds what just priced, so the effect skips that one re-seed. */
  const skipResumeRef = useRef(false);

  /* Pull the scenario doc — the only place `exceptionRequests[]` lives, and
     therefore the only way to know which findings already have an open request
     against them. Best-effort: a failed read must never look like a failed
     price, so it leaves the pending set empty rather than raising. */
  const refreshScenarioDoc = useCallback(
    (id) => {
      const target = id || scenarioIdRef.current;
      if (!target) return Promise.resolve(null);
      return sizerApi
        .getScenario(target)
        .then((doc) => {
          setScenarioDoc(doc || null);
          return doc;
        })
        .catch(() => null);
    },
    [],
  );

  /* RESUME — `?scenario=<id>`. Replays the STORED result rather than re-pricing:
     every priced run persists its full response, and re-POSTing to look at a
     saved scenario would price against today's sheet and append a version — the
     act of looking would change the record. */
  useEffect(() => {
    if (!resumeId) return undefined;
    // This is the id our own price just wrote to the URL, not a scenario
    // somebody navigated to — see `skipResumeRef`.
    if (skipResumeRef.current) {
      skipResumeRef.current = false;
      return undefined;
    }
    let live = true;
    sizerApi
      .getScenario(resumeId)
      .then((doc) => {
        if (!live) return;
        scenarioIdRef.current = doc?.scenarioId || resumeId;
        setScenarioDoc(doc || null);
        const pick = resumeVersion
          ? (doc?.versions || []).find((v) => v.version === resumeVersion)
          : null;
        const storedScenario = pick?.scenario || doc?.scenario;
        const vals = payloadToValues(storedScenario);
        setSeed(vals);
        setLoadedVersion(pick?.version || doc?.currentVersion || null);
        const currentV = (doc?.versions || []).find(
          (v) => v.version === doc?.currentVersion,
        );
        const priced = pick ? pick.result : doc?.result;
        setResult(priced || null);
        setPricedValues(priced ? vals : null);
        setPricedScenario(priced ? storedScenario : null);
        // The STORED timestamp, never now — on a resume this is history.
        setPricedAt(
          priced ? (pick || currentV)?.pricedAt || doc?.updatedAt || null : null,
        );
      })
      .catch((e) =>
        enqueueSnackbar(e?.response?.data?.detail || "Could not load that scenario", {
          variant: "error",
        }),
      );
    return () => {
      live = false;
    };
  }, [resumeId, resumeVersion, enqueueSnackbar]);

  const handlePrice = async (values) => {
    setPricePending(true);
    setPriceError(null);
    const body = {
      ...buildPayload(values),
      ...(scenarioIdRef.current ? { scenarioId: scenarioIdRef.current } : null),
    };
    try {
      const data = await sizerApi.price(body);
      if (data?.scenarioId) scenarioIdRef.current = data.scenarioId;
      const prevResult = result;
      const prevVersion = loadedVersion;
      setResult(data);
      setPricedValues(values);
      /* The posted body, as a stopgap until the doc re-read below lands. The
         response does NOT echo the scenario, and the body only carries the
         amounts when an override set them — the authoritative amounts are on
         the STORED (normalized) scenario, which the body reads off the doc. */
      setPricedScenario(body);
      setPricedAt(new Date().toISOString());
      // What just priced IS the current version now — the header names the one
      // this run created rather than the one it was resumed from.
      if (data?.scenarioVersion) setLoadedVersion(data.scenarioVersion);
      enqueueSnackbar(
        pricedToastText({ scenarioType: "rtl", data, prevResult, prevVersion }),
        {
          variant: "success",
          action: data?.scenarioId
            ? () => (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setMode(WORKSPACE_MODES.REVIEW)}
                  sx={{
                    border: 0,
                    background: "none",
                    color: "inherit",
                    font: "inherit",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  View results
                </Box>
              )
            : undefined,
        },
      );
      if (data?.scenarioId) {
        // The workspace header reads the doc (name, versions, status) and the
        // exception requests live on it — re-read it so the new version is in
        // the picker and a pending blocker survives the reprice.
        refreshScenarioDoc(data.scenarioId);
        /* Park the scenario in the URL so a reload comes back to this run —
           which is what makes a filed exception survive one. A blank sizer
           becomes this scenario's workspace; a resumed one drops a stale
           `&version=` (a reload would re-seed the old inputs). Either moves the
           URL, which must not re-seed. */
        if (resumeId !== data.scenarioId || searchParams.get("version")) {
          skipResumeRef.current = true;
          setSearchParams(
            { scenario: data.scenarioId, mode: WORKSPACE_MODES.EDIT },
            { replace: true },
          );
        }
      }
    } catch (e) {
      /* The requirements gate has its own 422 shape — `{missingRequirements:
         [{field, reason}]}` rather than a `detail` string — and a Loan Trade
         scenario without its note date lands there. Reading only `detail` turned
         a precise answer into "Failed to price this scenario." */
      const missing = e?.response?.data?.missingRequirements;
      setPriceError(
        new Error(
          (Array.isArray(missing) && missing.length
            ? missing.map((m) => m.reason || m.field).join(" · ")
            : null) ||
            e?.response?.data?.detail ||
            e?.message ||
            "Failed to price this scenario.",
        ),
      );
    } finally {
      setPricePending(false);
    }
  };

  const handleSave = async (values) => {
    setSavePending(true);
    try {
      const data = await sizerApi.save(
        buildPayload(values, { forSave: true, result }),
      );
      const dealId = data?._id || data?.id;
      enqueueSnackbar(dealId ? `Saved as loan ${dealId}` : "Loan saved", {
        variant: "success",
      });
      if (dealId && scenarioIdRef.current) {
        sizerApi
          .updateScenario(scenarioIdRef.current, {
            dealId,
            pipeline: values.pipelineTarget || "underwriting_pipeline",
          })
          .catch(() => {});
      }
      if (dealId) setTimeout(() => navigate(`/loan/${dealId}`), 700);
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.detail || e?.message || "Failed to save loan.",
        { variant: "error" },
      );
    } finally {
      setSavePending(false);
    }
  };

  const handleFileException = async (body) => {
    const doc = await sizerApi.createExceptionRequest(scenarioIdRef.current, body);
    setScenarioDoc(doc || null);
    enqueueSnackbar("Exception request filed", { variant: "success" });
    return doc;
  };

  return (
    <Form
      initialValues={EMPTY_SCENARIO}
      mutators={{ ...arrayMutators }}
      onSubmit={handlePrice}
    >
      {({ handleSubmit, values, form, invalid }) => (
        <GucFormBody
          handleSubmit={handleSubmit}
          values={values}
          form={form}
          invalid={invalid}
          seed={seed}
          resumeId={resumeId}
          mode={mode}
          onModeChange={setMode}
          onDuplicate={() => {
            // Same inputs, new scenario: drop the id and the doc (and with it
            // the exception requests filed against the original), keep the
            // form. The next price starts its own history.
            scenarioIdRef.current = null;
            setScenarioDoc(null);
            setSeed(null);
            setLoadedVersion(null);
            setResult(null);
            setPricedAt(null);
            setPricedValues(null);
            setPricedScenario(null);
            navigate("/sizers/guc");
            enqueueSnackbar("Duplicated — price it to save it as a new scenario", {
              variant: "success",
            });
          }}
          scenarioDoc={scenarioDoc}
          scenarioId={scenarioIdRef.current}
          loadedVersion={loadedVersion}
          onLoadVersion={(v) => {
            const vals = payloadToValues(v.scenario);
            setSeed(vals);
            setLoadedVersion(v.version);
            setVersionParam(v.version);
            setResult(v.result || null);
            setPricedValues(v.result ? vals : null);
            setPricedScenario(v.result ? v.scenario : null);
            setPricedAt(v.result ? v.pricedAt || null : null);
          }}
          onScenarioDocChange={setScenarioDoc}
          result={result}
          pricedAt={pricedAt}
          pricedValues={pricedValues}
          pricedScenario={pricedScenario}
          pricePending={pricePending}
          priceError={priceError}
          savePending={savePending}
          onSave={handleSave}
          onFileException={handleFileException}
        />
      )}
    </Form>
  );
}

/* ------------------------------------------------------- create confirm */

const DEFERRED_AT_CREATE = [
  "Exit strategy",
  "Broker origination points",
  "Interest calculation",
  "Dutch interest",
  "Condition at origination",
];

const SummaryRow = ({ k, v }) => (
  <Flx
    jb
    g={1.25}
    sx={{
      alignItems: "baseline",
      py: "7px",
      borderBottom: `1px dashed ${SZ.line}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>{k}</Txt>
    <Txt
      sx={{
        fontFamily: SZ.mono,
        fontVariantNumeric: "tabular-nums",
        fontSize: 12.5,
        fontWeight: 500,
        color: SZ.ink900,
        textAlign: "right",
      }}
    >
      {v || "—"}
    </Txt>
  </Flx>
);

function SaveDealConfirmDialog({ open, onClose, onConfirm, saving, values, amounts }) {
  const address = addressLabel(values.subjectProperties?.[0]?.address);
  const guarantors = (values.borrowers || []).length;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box sx={{ p: "18px 22px 8px" }}>
        <Txt component="div" sx={{ fontSize: 16, fontWeight: 700, color: SZ.ink900 }}>
          Create a loan from this scenario?
        </Txt>
        <Txt component="div" sx={{ fontSize: 12.5, color: SZ.ink500, mt: "3px" }}>
          This posts to the pipeline immediately and cannot be undone.
        </Txt>
      </Box>

      <Box sx={{ p: "8px 22px 0" }}>
        <SummaryRow k="Product" v="Ground Up Construction" />
        <SummaryRow k="Purpose" v={values.loanPurpose} />
        <SummaryRow k="Term" v={values.termMonths ? `${values.termMonths} months` : ""} />
        <SummaryRow
          k="Requested leverage"
          v={
            values.sizerInitialRequestedLeverage != null
              ? fmtPct(values.sizerInitialRequestedLeverage, 2)
              : ""
          }
        />
        <SummaryRow
          k="Initial advance"
          v={amounts.advance > 0 ? fmtMoney(amounts.advance) : ""}
        />
        <SummaryRow
          k="Construction holdback"
          v={amounts.holdback > 0 ? fmtMoney(amounts.holdback) : ""}
        />
        <SummaryRow
          k="Total loan amount"
          v={amounts.total > 0 ? fmtMoney(amounts.total) : ""}
        />
        <SummaryRow k="Entity" v={values.entity?.entityName} />
        <SummaryRow
          k="Guarantors"
          v={`${guarantors} guarantor${guarantors === 1 ? "" : "s"}`}
        />
        <SummaryRow k="Property" v={address} />
        {values.gcCompletionGuaranty && (
          <SummaryRow k="GC completion guaranty" v="Yes" />
        )}
      </Box>

      <Box sx={{ p: "16px 22px 0" }}>
        <Callout tone="warn" title="Collected later, not on this loan">
          {DEFERRED_AT_CREATE.join(" · ")} — plus guarantor names and addresses
          where they were left blank.
        </Callout>
      </Box>

      <Flx jb ac g={1.5} sx={{ p: "18px 22px 20px", flexWrap: "wrap" }}>
        <ActionNote>The scenario stays in the pipeline either way.</ActionNote>
        <Flx ac g={1.25}>
          <SecondaryButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={onConfirm}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {saving ? "Creating…" : "Create loan"}
          </PrimaryButton>
        </Flx>
      </Flx>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ body */

function GucFormBody({
  handleSubmit,
  values,
  form,
  invalid,
  seed,
  resumeId,
  mode,
  onModeChange,
  onDuplicate,
  scenarioDoc,
  scenarioId,
  loadedVersion,
  onLoadVersion,
  onScenarioDocChange,
  result,
  pricedAt,
  pricedValues,
  pricedScenario,
  pricePending,
  priceError,
  savePending,
  onSave,
  onFileException,
}) {
  const shellRef = useRef(null);
  const { spyOpen, toggleSpy, payloadOpen, togglePayload } = useSizerDevTools();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [exceptionPrefill, setExceptionPrefill] = useState(null);

  // The version on screen. Its STORED scenario is the normalized one — it
  // carries the backend-derived baseLoanAmount / totalHoldback /
  // totalLoanAmount, which neither the form values nor the posted body do.
  const shownRun = (scenarioDoc?.versions || []).find((v) => v.version === loadedVersion);
  const shownScenario =
    shownRun?.scenario ||
    (loadedVersion && loadedVersion === scenarioDoc?.currentVersion
      ? scenarioDoc?.scenario
      : null);

  // Priced when the response / stored scenario has the amounts, otherwise the
  // estimate — the same decision the RTL sizer makes (sizersShared/sizerAmounts).
  const amounts = useMemo(
    () =>
      shownAmounts({
        result,
        scenario: shownScenario || pricedScenario,
        pricedValues,
        values,
      }),
    [result, shownScenario, pricedScenario, pricedValues, values],
  );
  const state = amounts.state;

  /* What the numbers on screen were priced from — the version the form was
     loaded from, or the last run this session. The workspace diffs the form
     against it for the "N changes since vN" card, the stale rail and the Save
     as deal gate, so the amounts strip no longer tracks staleness itself: the
     rail it sits in is dimmed and tagged "Stale" as a whole. */
  const baseline = pricedValues || seed || null;


  const cures = useMemo(() => readCures(result, values), [result, values]);
  const pendingCodes = useMemo(() => pendingExceptionCodes(scenarioDoc), [scenarioDoc]);
  const zeroExperienceBlocked = useMemo(
    () => isGucZeroExperienceBlock(result),
    [result],
  );
  const blockerPending = pendingCodes.includes("guc_no_ground_up_experience");

  // Scroll back to the top after a run — the answer renders in a rail that
  // starts up there.
  useEffect(() => {
    if (pricedAt) scrollSizerToTop(shellRef.current);
  }, [pricedAt]);

  /* Seed the form from a resumed scenario — ONCE per scenario. The guard is the
     point: react-final-form hands a NEW `form` object to the render prop on
     every render, so an effect depending on it re-runs on every keystroke — and
     this one calls `reset`, which would write the stored value straight back
     over what had just been typed. */
  const seededRef = useRef(null);
  useEffect(() => {
    if (!seed || seededRef.current === seed) return;
    seededRef.current = seed;
    form.reset(seed);
  }, [seed, form]);

  // A cure clicked in Review (or in the Explain dialog opened from it) writes a
  // form input — so the page has to move to where that input is.
  const toEdit = () => {
    if (resumeId && mode !== WORKSPACE_MODES.EDIT) onModeChange?.(WORKSPACE_MODES.EDIT);
  };

  /** A reprice suggestion writes the ACTUAL input — spec §1.4. */
  const repriceAt = (amount) => {
    form.change("baseLoanAmount", Math.round(amount));
    form.change(SIZE_BY_FIELD, SIZE_BY.AMOUNT);
    // The override wins over leverage server-side anyway (`if lev and not
    // baseLoanAmount`); clearing it keeps the form from claiming two answers.
    form.change("sizerInitialRequestedLeverage", undefined);
    setReviewOpen(false);
    toEdit();
    scrollSizerToTop(shellRef.current);
  };

  const applyBudgetShare = (pct) => {
    form.change("financedBudgetPercent", pct);
    toEdit();
    scrollSizerToTop(shellRef.current);
  };

  /**
   * The guaranty toggle opens the prefilled request — but only where the deal is
   * actually blocked for zero ground-up experience. On a deal that classifies
   * fine, the guaranty is just a structural fact about the project and there is
   * nothing to except.
   */
  const onGuarantyTurnedOn = () => {
    if (!zeroExperienceBlocked) return;
    if (blockerPending) return;
    setExceptionPrefill(gucGuarantyExceptionPrefill({ result, values }));
  };

  const pricedContext = useMemo(() => {
    const v = pricedValues || {};
    return [v.loanPurpose, v.termMonths ? `${v.termMonths} mo` : null]
      .filter(Boolean)
      .join(" · ");
  }, [pricedValues]);

  return (
    <SizerWorkspace
      rootRef={shellRef}
      adminTools={
        <SizerDevBar
          variant="menu"
          onFillDemo={() => form.reset({ ...EMPTY_SCENARIO, ...DEMO_SCENARIO })}
          spyOpen={spyOpen}
          onToggleSpy={toggleSpy}
          payloadOpen={payloadOpen}
          onTogglePayload={togglePayload}
        />
      }
      onExplain={() => setReviewOpen(true)}
      title="GUC Sizer"
      subtitle="Size and price a ground-up construction loan. Same engine as the RTL Sizer, with the answer said in fewer words."
      scenarioId={resumeId}
      doc={scenarioDoc}
      onDocChange={onScenarioDocChange}
      typeLabel="GUC"
      shownVersion={loadedVersion}
      onLoadVersion={onLoadVersion}
      mode={mode}
      onModeChange={onModeChange}
      onDuplicate={resumeId ? onDuplicate : null}
      values={values}
      baseline={baseline}
      onDiscard={() => baseline && form.reset(baseline)}
      result={result}
      pricePending={pricePending}
      priceError={priceError}
      onPrice={() => handleSubmit()}
      blockedReason={
        invalid
          ? "Fill the required fields marked * — a missing one would silently misprice the scenario."
          : null
      }
      liveNote="Priced against the live RTL rate sheet."
      onSave={() => setConfirmOpen(true)}
      savePending={savePending}
      kpis={
        <GucKpis
          result={result}
          scenario={shownScenario || pricedScenario}
          version={loadedVersion}
          amounts={result ? amounts : null}
        />
      }
      panels={
        <GucPanels
          result={result}
          cures={cures}
          onRepriceAt={repriceAt}
          onApplyBudgetShare={applyBudgetShare}
          onExplain={() => setReviewOpen(true)}
          blocker={
            zeroExperienceBlocked ? (
              <ZeroExperienceNote pending={blockerPending} inReview />
            ) : null
          }
        />
      }
      inputCells={scenarioInputSummary("rtl", shownScenario || pricedScenario)}
      form={
        <Box component="form" onSubmit={handleSubmit}>
          {/* Plain cards, never collapsible ones. Pricing is an iterate loop —
              change a number, re-price, look again — so the inputs stay put and
              stay open beside the answer. */}
          <Flx column g={2.5}>
            <FormCard>
              <GucLoanDetailsSection
                first
                sizing={result?.sizing}
                onGuarantyTurnedOn={onGuarantyTurnedOn}
                onClearOverride={() => {
                  form.change("baseLoanAmount", undefined);
                  form.change(SIZE_BY_FIELD, SIZE_BY.LEVERAGE);
                }}
              />
            </FormCard>
            <FormCard>
              <EntitySection first />
            </FormCard>
            <FormCard>
              <GucGuarantorsSection first />
            </FormCard>
            <FormCard>
              <GucPropertySection
                first
                result={result}
              />
            </FormCard>
          </Flx>

          {/* Dev tooling last, under the form it acts on. */}

          <RffFormSpy formSpy={spyOpen} />
          <SizerPayloadPanel
            enabled={payloadOpen}
            sections={[
              {
                label: "Price",
                endpoint: "POST /pricing/scenario/rtl",
                note: "Scenario-stage stubs included — the backend reconciles them into dataChecks.",
                value: buildPayload(values),
              },
              {
                label: "Create loan",
                endpoint: "POST /underwriting/create_loan",
                note: "Stubs stripped; the GC guaranty maps onto generalContractors[0].",
                value: buildPayload(values, { forSave: true, result }),
              },
            ]}
          />
        </Box>
      }
      rail={
        <Flx column g={1.75}>
          {/* The form beside it is editable, so the answer says WHEN it was the
              answer. */}
          {pricedAt && (
            <Txt sx={{ fontSize: 11, color: SZ.ink400 }}>
              Priced {formatDateTime(pricedAt)}
              {pricedContext ? ` · ${pricedContext}` : ""}
            </Txt>
          )}

          {/* "Explain this result" here is the way into the verbose dialog —
              every finding's Request exception button lives there. */}
          <AmountsStrip
            amounts={amounts}
            state={state}
            result={result}
            onExplain={() => setReviewOpen(true)}
          />

          {zeroExperienceBlocked && <ZeroExperienceNote pending={blockerPending} />}

          <GucResultsPanel
            result={result}
            pending={pricePending}
            error={null}
            cures={cures}
            onRepriceAt={repriceAt}
            onApplyBudgetShare={applyBudgetShare}
          />
        </Flx>
      }
    >
      <SaveDealConfirmDialog
        open={confirmOpen}
        saving={savePending}
        values={values}
        amounts={amounts}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await onSave(values);
          setConfirmOpen(false);
        }}
      />

      {/* "Explain this result" — a floating panel (bottom-right), not a modal,
          so the results stay readable beside it. */}
      <ExplainResultPanel
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        result={result}
        version={loadedVersion}
        pricedAt={pricedAt}
        scenarioId={scenarioId}
        pendingCodes={pendingCodes}
        onRequestException={(finding) =>
          setExceptionPrefill({
            severity: finding.severity,
            code: finding.code,
            exceptionCategory: "Leverage",
            riskBucket: "Credit Structure",
            exceptionDescription: finding.message,
            citation: finding.citation,
            observedValue: finding.observedValue,
            threshold: finding.threshold,
            requiredDocuments: finding.requiredDocuments,
          })
        }
        onRepriceAt={repriceAt}
      />

      <RequestExceptionModal
        open={Boolean(exceptionPrefill)}
        onClose={() => setExceptionPrefill(null)}
        onSubmit={onFileException}
        scenarioId={scenarioId}
        prefill={exceptionPrefill}
      />
    </SizerWorkspace>
  );
}
